import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Space, Spin, Tag, Typography, message } from 'antd';
import { EditOutlined, DownloadOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';

const { Text, Paragraph } = Typography;

// The page app (backend sdk_apps/page.py): one Markdown document. The backend
// parses it into blocks and resolves each embed for this viewer, so this only
// draws what it is given — a hidden embed never carries its content.
//
// `linkOrigin`: the page is being shown as an embed block on another site
// (components/Embed/), so an embedded entity or app is a plain link to the
// web app at that origin, opened in a new tab instead of navigating inside
// the frame.
function EmbedLink({ to, linkOrigin, children }) {
    if (linkOrigin) {
        return <a className="page-embed" href={`${linkOrigin}${to}`} target="_blank" rel="noopener noreferrer">{children}</a>;
    }
    return <Link className="page-embed" to={to}>{children}</Link>;
}

function Embed({ block, linkOrigin }) {
    if (block.kind === 'entity') {
        const e = block.entity;
        return (
            <EmbedLink to={`/entity/${e.id}`} linkOrigin={linkOrigin}>
                <Space size={6}><Text strong>{e.name}</Text><Tag>{e.entity_type}</Tag></Space>
                {e.description && <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ margin: '4px 0 0' }}>{e.description}</Paragraph>}
            </EmbedLink>
        );
    }
    if (block.kind === 'app') {
        return (
            <EmbedLink to={`/app/${block.app.id}`} linkOrigin={linkOrigin}>
                <Space size={6}><Text strong>{block.app.name}</Text><Tag>{block.app.app_kind}</Tag><Text type="secondary">{block.app.view}</Text></Space>
            </EmbedLink>
        );
    }
    if (block.kind === 'hidden') {
        return <div className="page-embed page-embed--hidden">Not visible to you</div>;
    }
    return <div className="page-embed"><a href={block.url} target="_blank" rel="noopener noreferrer">{block.url}</a></div>;
}

function Block({ block, linkOrigin }) {
    switch (block.type) {
        case 'heading': {
            const H = `h${block.level}`;
            return <H>{block.text}</H>;
        }
        case 'list':
            return <ul>{block.items.map((item, i) => <li key={i}>{item}</li>)}</ul>;
        case 'embed':
            return <Embed block={block} linkOrigin={linkOrigin} />;
        default:
            return <p>{block.text}</p>;
    }
}

// The document read-only, as the page shows it and as its embed block
// (backend services/embed_blocks.py: the page's embed view is `document`)
// shows it on other sites.
export function PageBlocks({ blocks, empty = 'An empty page.', linkOrigin = null }) {
    return (
        <div className="page-doc">
            {blocks.length === 0 && <Text type="secondary">{empty}</Text>}
            {blocks.map((b, i) => <Block key={i} block={b} linkOrigin={linkOrigin} />)}
        </div>
    );
}

function PageApp({ instance }) {
    const api = useApi();
    const [doc, setDoc] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => api.get(`/api/apps/${instance.id}/views/document`)
        .then(r => setDoc(r.data))
        .catch(() => message.error('Could not load this page')), [api, instance.id]);

    useEffect(() => { load(); }, [load]);

    const save = () => {
        setSaving(true);
        api.post(`/api/apps/${instance.id}/verbs/write`, { markdown: draft, revision: doc.revision })
            .then(() => { setEditing(false); return load(); })
            .catch(err => {
                if (err.status === 409) {
                    message.warning('Someone saved this page after you opened it. Your text is still in the editor; reload to see theirs.');
                } else {
                    message.error(err.message || 'Could not save the page');
                }
            })
            .finally(() => setSaving(false));
    };

    const download = () => {
        const blob = new Blob([doc.markdown], { type: 'text/markdown' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${instance.name || 'page'}.md`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    if (!doc) return <Spin />;

    if (editing) {
        return (
            <div className="page-editor">
                <Input.TextArea
                    autoSize={{ minRows: 12 }}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    placeholder={'# A heading\n\nText as you would write it.\n\nA link on its own line is an embed:\n/entity/12'}
                />
                <Space style={{ marginTop: 12 }}>
                    <Button type="primary" loading={saving} onClick={save}>Save</Button>
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                    <Button type="link" onClick={load}>Reload</Button>
                </Space>
            </div>
        );
    }

    return (
        <div>
            <Space style={{ marginBottom: 12 }}>
                <Button icon={<EditOutlined />} onClick={() => { setDraft(doc.markdown); setEditing(true); }}>Edit</Button>
                <Button icon={<DownloadOutlined />} onClick={download}>Markdown</Button>
            </Space>
            <PageBlocks blocks={doc.blocks} empty="An empty page. Edit to write it." />
        </div>
    );
}

export default PageApp;
